import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import './PlayerHomeDashboard.css';

const formatPracticeDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const PlayerHomeDashboard = ({ userId, limit = 10, title = 'Upcoming practices' }) => {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPractices = useCallback(async () => {
    if (!userId) {
      setPractices([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await api.getUpcomingPracticesForUser(userId, limit);
      setPractices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load upcoming practices');
      setPractices([]);
    } finally {
      setLoading(false);
    }
  }, [userId, limit]);

  useEffect(() => {
    loadPractices();
  }, [loadPractices]);

  if (!userId) {
    return null;
  }

  return (
    <section className="player-home-dashboard" aria-label="Upcoming practices">
      <div className="player-home-dashboard__header">
        <div>
          <h2>{title}</h2>
          <p>Your next scheduled practices across all clubs.</p>
        </div>
        <Link to={soccerPath('/clubs/list')} className="player-home-dashboard__view-all">
          Browse clubs →
        </Link>
      </div>

      {loading && <p className="player-home-dashboard__status">Loading practices…</p>}
      {error && !loading && (
        <p className="player-home-dashboard__status player-home-dashboard__status--error">{error}</p>
      )}

      {!loading && !error && practices.length === 0 && (
        <p className="player-home-dashboard__status">
          No upcoming practices yet. Join a club or check back when your admin schedules sessions.
        </p>
      )}

      {!loading && !error && practices.length > 0 && (
        <ul className="player-home-dashboard__list">
          {practices.map((practice) => (
            <li key={practice.id} className="player-home-dashboard__card">
              <div className="player-home-dashboard__card-main">
                <p className="player-home-dashboard__club">{practice.clubName}</p>
                <h3>{practice.title}</h3>
                <p className="player-home-dashboard__date">{formatPracticeDate(practice.practiceDate)}</p>
                {practice.location && (
                  <p className="player-home-dashboard__location">{practice.location}</p>
                )}
                {practice.seriesId && (
                  <span className="player-home-dashboard__badge">Recurring series</span>
                )}
              </div>
              <div className="player-home-dashboard__actions">
                <Link
                  to={soccerPath(`/practices/${practice.id}`)}
                  className="btn btn-primary"
                >
                  Vote availability
                </Link>
                <Link
                  to={soccerPath(`/clubs/${practice.clubId}`)}
                  className="btn btn-secondary"
                >
                  View club
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default PlayerHomeDashboard;
